import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { 
  Car, 
  Calendar, 
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  ChevronRight,
  Loader2
} from "lucide-react";

import { API } from "../utils/api";

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState({ total_spots: 0, my_bookings: 0, received_bookings: 0, total_earnings: 0, total_spent: 0 });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moneyModalOpen, setMoneyModalOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/stats/dashboard`),
        axios.get(`${API}/bookings/my`)
      ]);
      if (statsRes.data) setStats(statsRes.data);
      if (bookingsRes.data) setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    }
  };

  if (authLoading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  if (!user) return null;

  const earnings = stats?.total_earnings || 0;
  const spent = stats?.total_spent || 0;

  const statCards = [
    {
      title: "Mis Plazas",
      value: stats?.my_spots || 0,
      icon: Car,
      gradient: "stats-gradient-1",
      link: "/my-spots"
    },
    {
      title: "Mis Reservas",
      value: stats?.my_bookings || 0,
      icon: Calendar,
      gradient: "stats-gradient-2",
      link: "/my-bookings"
    },
    {
      title: "Reservas Recibidas",
      value: stats?.received_bookings || 0,
      icon: TrendingUp,
      gradient: "stats-gradient-3",
      link: "/my-spots"
    },
    {
      title: "Dinero",
      money: true,
      earnings,
      spent,
      icon: Wallet,
      gradient: "stats-gradient-4",
    }
  ];

  return (
    <div className="pt-20 min-h-screen" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Hola, {user?.full_name || user?.username || user?.email?.split('@')[0] || "Usuario"}
            </h1>
            <p className="text-gray-400">Gestiona tus plazas y reservas</p>
          </div>
          <Link to="/add-spot">
            <Button className="bg-violet-500 hover:bg-violet-600 text-white glow-violet-hover" data-testid="add-spot-btn">
              <Plus className="w-5 h-5 mr-2" />
              Publicar Plaza
            </Button>
          </Link>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 items-stretch">
          {statCards.map((stat, index) => {
            // Tarjeta especial "Dinero" — abre un modal en lugar de navegar
            if (stat.money) {
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setMoneyModalOpen(true)}
                  className="text-left h-full"
                  data-testid="stat-dinero"
                >
                  <Card className={`dashboard-card ${stat.gradient} card-hover h-full`}>
                    <CardContent className="p-6 h-full flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
                          <stat.icon className="w-6 h-6 text-violet-400" />
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      </div>
                      <p className="text-gray-400 text-sm mb-2">{stat.title}</p>
                      <div className="flex items-center gap-3 flex-wrap mt-auto">
                        <span className="flex items-center gap-1 text-emerald-400 font-bold text-base price-display">
                          <ArrowUpRight className="w-4 h-4" />
                          €{stat.earnings.toFixed(2)}
                        </span>
                        <span className="flex items-center gap-1 text-rose-400 font-bold text-base price-display">
                          <ArrowDownRight className="w-4 h-4" />
                          €{stat.spent.toFixed(2)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </button>
              );
            }
            // Tarjetas normales
            return (
              <Link key={index} to={stat.link} className="h-full block">
                <Card className={`dashboard-card ${stat.gradient} card-hover h-full`} data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
                  <CardContent className="p-6 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center">
                        <stat.icon className="w-6 h-6 text-violet-400" />
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                    <p className="text-gray-400 text-sm mb-1">{stat.title}</p>
                    <p className="text-2xl font-bold text-white price-display mt-auto">{stat.value}</p>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Money detail modal */}
        <Dialog open={moneyModalOpen} onOpenChange={setMoneyModalOpen}>
          <DialogContent className="bg-[#231f30] border-white/10 text-white max-w-md" data-testid="money-modal">
            <DialogHeader>
              <DialogTitle className="text-white text-xl flex items-center gap-2">
                <Wallet className="w-5 h-5 text-violet-400" />
                Tu dinero en Plazafy
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                Selecciona qué quieres consultar
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              <Link
                to="/earnings"
                onClick={() => setMoneyModalOpen(false)}
                className="group p-5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-500/15 transition-all text-left block"
                data-testid="money-modal-ingresos-btn"
              >
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  <ArrowUpRight className="w-4 h-4 text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-gray-300 text-sm mb-1">Ingresos</p>
                <p className="text-emerald-400 text-2xl font-bold price-display">
                  €{earnings.toFixed(2)}
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  Dinero cobrado por reservas en tus plazas
                </p>
              </Link>
              <Link
                to="/expenses"
                onClick={() => setMoneyModalOpen(false)}
                className="group p-5 rounded-xl bg-rose-500/10 border border-rose-500/20 hover:border-rose-500/40 hover:bg-rose-500/15 transition-all text-left block"
                data-testid="money-modal-gastos-btn"
              >
                <div className="flex items-center justify-between mb-2">
                  <TrendingDown className="w-5 h-5 text-rose-400" />
                  <ArrowUpRight className="w-4 h-4 text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-gray-300 text-sm mb-1">Gastos</p>
                <p className="text-rose-400 text-2xl font-bold price-display">
                  €{spent.toFixed(2)}
                </p>
                <p className="text-gray-500 text-xs mt-2">
                  Dinero pagado por reservar plazas de otros
                </p>
              </Link>
            </div>
          </DialogContent>
        </Dialog>

        {/* Recent Bookings */}
        <div className="dashboard-card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-white">Reservas Recientes</h2>
            <Link 
              to="/my-bookings" 
              className="text-violet-400 hover:text-violet-300 flex items-center gap-1 text-sm"
              data-testid="view-all-bookings"
            >
              Ver todas
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {recentBookings.length > 0 ? (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <div 
                  key={booking.id}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={booking.parking_spot_image || "https://images.unsplash.com/photo-1548343361-02248be15911?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{booking.parking_spot_title}</p>
                    <p className="text-gray-400 text-sm truncate">{booking.parking_spot_address}</p>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(booking.start_date).toLocaleDateString('es-ES')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="price-display text-white">€{booking.total_price.toFixed(2)}</p>
                    <span className={`inline-block px-2 py-1 rounded text-xs mt-1 status-${booking.status}`}>
                      {booking.status === "confirmed" ? "Confirmada" : booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No tienes reservas aún</p>
              <Link to="/explore">
                <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
                  Explorar Plazas
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-white mb-4">Acciones Rápidas</h3>
            <div className="space-y-3">
              <Link to="/add-spot" className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-violet-400" />
                </div>
                <div>
                  <p className="text-white">Publicar nueva plaza</p>
                  <p className="text-sm text-gray-500">Empieza a ganar dinero</p>
                </div>
              </Link>
              <Link to="/explore" className="flex items-center gap-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                  <Car className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white">Buscar aparcamiento</p>
                  <p className="text-sm text-gray-500">Encuentra tu plaza ideal</p>
                </div>
              </Link>
            </div>
          </div>

          <div className="dashboard-card">
            <h3 className="text-lg font-semibold text-white mb-4">Tu Perfil</h3>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-violet-500/20 flex items-center justify-center">
                <span className="text-2xl font-bold text-violet-400">
                  {(user?.full_name || user?.username || user?.email || "U").charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-white font-medium">{user?.full_name || user?.username || user?.email?.split('@')[0] || "Usuario"}</p>
                <p className="text-gray-400 text-sm">{user.email}</p>
              </div>
            </div>
            <div className="pt-4 border-t border-white/5">
              <p className="text-sm text-gray-500">
                Miembro desde {new Date(user.created_at || Date.now()).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
